import { computeAgeYears, exponentialDecay, linearDecay, roundToNearest } from './gvMath';
import { MANUFACTURERS, DEPRECIATION, ADJUSTMENTS } from '../config/altimeterConfig';

export const calculateAltimeter = ({ manufacturer, model, domYear, condition, type, customNewPriceEur }) => {
  const notes = [];
  const adjustments = {};
  let newPriceEur;
  let altiType;
  let mfrCfg = null;
  let brandKey;

  if (customNewPriceEur) {
    newPriceEur = customNewPriceEur;
    altiType = type || 'digital';
    brandKey = null;
    notes.push('Custom price used. Brand and version adjustments not applied.');
  } else {
    const mfrKey = manufacturer.toLowerCase().trim();
    const mdlKey = model.toLowerCase().trim();
    mfrCfg = MANUFACTURERS[mfrKey];
    if (!mfrCfg) {
      throw new Error(`Unknown manufacturer: ${manufacturer}. Expected: ${Object.keys(MANUFACTURERS).join(', ')}`);
    }
    const modelCfg = mfrCfg.models[mdlKey];
    if (!modelCfg) {
      throw new Error(`Unknown model: ${model}. Expected: ${Object.keys(mfrCfg.models).join(', ')}`);
    }
    newPriceEur = modelCfg.new_price_eur;
    altiType = modelCfg.type || 'digital';
    brandKey = mfrCfg.brand_key;
  }

  // Age depreciation
  const ageYears = computeAgeYears(domYear);
  let ageRetention;

  if (altiType === 'analogue') {
    const cfg = DEPRECIATION.analogue;
    ageRetention = ageYears <= 0 ? 1.0 : linearDecay(ageYears, cfg.end_of_life_years, cfg.floor);
  } else if (altiType === 'audible') {
    const cfg = DEPRECIATION.audible;
    ageRetention = ageYears <= 0 ? 1.0 : exponentialDecay(ageYears, cfg.lambda, cfg.floor);
  } else {
    const cfg = DEPRECIATION.digital;
    ageRetention = ageYears <= 0 ? 1.0 : exponentialDecay(ageYears, cfg.lambda, cfg.floor);
  }

  const ageValueEur = newPriceEur * ageRetention;
  adjustments.ageRetention = Math.round(ageRetention * 1000) / 1000;

  // Condition multiplier
  let conditionMult;
  if (condition != null) {
    conditionMult = ADJUSTMENTS.condition[condition.toLowerCase().trim()] ?? ADJUSTMENTS.condition._default;
  } else {
    conditionMult = ADJUSTMENTS.condition._default;
    notes.push("Condition unknown. Assuming 'used' (baseline 0.85).");
  }
  if (conditionMult !== 1.0) {
    adjustments.condition = conditionMult;
  }

  // Brand attractiveness
  let brandMult;
  if (customNewPriceEur) {
    brandMult = 1.0;
  } else if (brandKey) {
    brandMult = ADJUSTMENTS.brand_attractiveness[brandKey] ?? ADJUSTMENTS.brand_attractiveness._default;
  } else {
    brandMult = ADJUSTMENTS.brand_attractiveness._unknown;
    notes.push('Unknown brand - 5% desirability penalty applied.');
  }
  if (brandMult !== 1.0) {
    adjustments.brandAttractiveness = brandMult;
  }

  // Version penalty
  let versionMult;
  if (customNewPriceEur) {
    versionMult = 1.0;
  } else {
    const mdlKey = model.toLowerCase().trim();
    versionMult = ADJUSTMENTS.version_penalty[mdlKey] ?? ADJUSTMENTS.version_penalty._default;
  }
  if (versionMult !== 1.0) {
    adjustments.versionPenalty = versionMult;
    notes.push(`Version penalty: ${Math.round((1 - versionMult) * 100)}% - newer version exists.`);
  }

  // Final value
  let valueEur = ageValueEur * conditionMult * brandMult * versionMult;

  // Round to nearest 5, enforce minimum 10
  valueEur = roundToNearest(valueEur, 5);
  valueEur = Math.max(valueEur, 10);

  const retentionPct = newPriceEur > 0 ? Math.round((valueEur / newPriceEur) * 1000) / 10 : 0;

  return {
    valueEur,
    newPriceEur: Math.round(newPriceEur * 100) / 100,
    retentionPct,
    notes,
    adjustments,
  };
};
